/**
 * Gallery proofing — clients pick favorites + leave comments.
 *
 * Identifier strategy: unauthenticated portal clients are tagged with
 * a random UUID stored in localStorage (per origin per browser). The
 * studio side uses the studio user's session — RLS handles access
 * gating. We don't ask clients to "log in" to the portal; the
 * gallery URL itself is the credential, and the localStorage UUID
 * just lets them un-pick what they previously picked.
 *
 * Realtime: picks + comments are added to the supabase_realtime
 * publication, so studio workspace can subscribe and watch picks
 * appear live as the client clicks through.
 *
 * DEMO-4 — every hook here now has a demo branch backed by
 * DemoStoreContext (`galleryPicks` / `galleryComments`). That's what
 * closes the keynote loop: heart a photo on `/gallery/demo-g-…`, walk
 * back to the editor, and the Proofing canvas shows the pick with the
 * picker's name on it. Demo-scoped ids only — a pick or comment aimed
 * at a real gallery while Preview Studio is on is refused rather than
 * written (see src/lib/demoScope.ts).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// SEC-PORTAL-2 — portal READS go through a session-less client so a
// visitor who has their own Orbit account isn't refused by the
// anon-only policies. See integrations/supabase/portal.ts.
import { portalReads } from '@/integrations/supabase/portal';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { assertDemoScoped, isDemoId } from '@/lib/demoScope';

// ─── Types ───────────────────────────────────────────────────────

export interface GalleryPick {
  id: string;
  photo_id: string;
  gallery_id: string;
  picker_identifier: string;
  picker_name: string | null;
  created_at: string;
}

export interface GalleryComment {
  id: string;
  photo_id: string;
  gallery_id: string;
  author_identifier: string;
  author_name: string | null;
  body: string;
  resolved: boolean;
  created_at: string;
}

// ─── Picker identity (portal side) ───────────────────────────────

/**
 * Stable per-browser UUID used to tag picks/comments from
 * unauthenticated portal sessions. Persists across reloads.
 */
const PICKER_KEY = 'orbit.gallery.pickerId';
const PICKER_NAME_KEY = 'orbit.gallery.pickerName';

export function getPickerIdentifier(): string {
  if (typeof window === 'undefined') return 'ssr';
  let id = window.localStorage.getItem(PICKER_KEY);
  if (!id) {
    id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    window.localStorage.setItem(PICKER_KEY, id);
  }
  return id;
}

export function getPickerName(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(PICKER_NAME_KEY);
}

export function setPickerName(name: string) {
  if (typeof window === 'undefined') return;
  if (name.trim().length === 0) {
    window.localStorage.removeItem(PICKER_NAME_KEY);
    return;
  }
  window.localStorage.setItem(PICKER_NAME_KEY, name.trim());
}

// ─── Picks: read ─────────────────────────────────────────────────

/**
 * All picks for a gallery (studio view). Returns a Map<photoId, count>
 * via the convenience helpers below; raw rows are kept for the
 * "see who picked what" detail UI.
 */
export const useGalleryPicks = (galleryId: string | undefined) => {
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['gallery-picks', galleryId, demoStore.version],
    queryFn: async () => {
      if (!galleryId) return [];
      // DEMO-4 — demo ids never reach Supabase: `gallery_id` is a uuid
      // column, so `.eq('gallery_id', 'demo-g-1')` errors outright.
      if (isDemoId(galleryId)) {
        return demoStore.galleryPicks
          .list()
          .filter((p) => p.gallery_id === galleryId)
          .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))) as unknown as GalleryPick[];
      }
      const { data, error } = await portalReads()
        .from('gallery_picks')
        .select('*')
        .eq('gallery_id', galleryId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as GalleryPick[];
    },
    enabled: !!galleryId,
  });
};

/**
 * Just the picks for the *current* portal session — used to highlight
 * which photos the client has already favorited.
 */
export const useMyPicks = (galleryId: string | undefined) => {
  const pickerId = getPickerIdentifier();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['gallery-picks', galleryId, 'mine', pickerId, demoStore.version],
    queryFn: async () => {
      if (!galleryId) return [];
      if (isDemoId(galleryId)) {
        return demoStore.galleryPicks
          .list()
          .filter(
            (p) =>
              p.gallery_id === galleryId && p.picker_identifier === pickerId,
          ) as unknown as GalleryPick[];
      }
      const { data, error } = await portalReads()
        .from('gallery_picks')
        .select('*')
        .eq('gallery_id', galleryId)
        .eq('picker_identifier', pickerId);
      if (error) throw error;
      return data as GalleryPick[];
    },
    enabled: !!galleryId,
  });
};

// ─── Picks: write ────────────────────────────────────────────────

/**
 * Toggle a pick for the current portal session. If the photo is
 * already picked, removes it; otherwise inserts a new row.
 */
export const useTogglePick = (galleryId: string) => {
  const queryClient = useQueryClient();
  const pickerId = getPickerIdentifier();
  const { isDemo, canUseDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({
      photoId,
      currentlyPicked,
    }: {
      photoId: string;
      currentlyPicked: boolean;
    }) => {
      // DEMO-4 — a heart on a demo gallery lands in the demo store, so
      // the studio side actually sees it when they navigate back. The
      // `canUseDemo` check mirrors `usePublicGalleryById`: only a
      // demo-granted viewer can reach a demo gallery in the first
      // place, so nobody else can write picks against one.
      if (isDemoId(galleryId)) {
        if (!isDemo && !canUseDemo) {
          throw new Error('This gallery is not available.');
        }
        if (currentlyPicked) {
          const existing = demoStore.galleryPicks
            .list()
            .find(
              (p) =>
                p.gallery_id === galleryId &&
                p.photo_id === photoId &&
                p.picker_identifier === pickerId,
            );
          if (existing) demoStore.galleryPicks.remove(existing.id as string);
          return;
        }
        demoStore.galleryPicks.add({
          id: `demo-gp-${crypto.randomUUID().slice(0, 8)}`,
          gallery_id: galleryId,
          photo_id: photoId,
          picker_identifier: pickerId,
          picker_name: getPickerName(),
          created_at: new Date().toISOString(),
        });
        return;
      }

      // SEC-1 lockdown: the anon insert/delete policies on
      // gallery_picks are gone. The RPC enforces is_public /
      // proofing_enabled / proofing_locked + the pick limit
      // server-side; p_picked=true inserts, false removes.
      // Cast through `any` — types.ts isn't regenerated for the
      // SEC-1 RPCs yet (same pattern as deliveryService).
      const { error } = await (supabase as any).rpc('public_gallery_toggle_pick', {
        p_gallery_id: galleryId,
        p_photo_id: photoId,
        p_picker_identifier: pickerId,
        p_picker_name: getPickerName(),
        p_picked: !currentlyPicked,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-picks', galleryId] });
    },
  });
};

// ─── Comments: read ──────────────────────────────────────────────

export const useGalleryComments = (galleryId: string | undefined) => {
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['gallery-comments', galleryId, demoStore.version],
    queryFn: async () => {
      if (!galleryId) return [];
      if (isDemoId(galleryId)) {
        return demoStore.galleryComments
          .list()
          .filter((c) => c.gallery_id === galleryId)
          .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))) as unknown as GalleryComment[];
      }
      const { data, error } = await portalReads()
        .from('gallery_comments')
        .select('*')
        .eq('gallery_id', galleryId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as GalleryComment[];
    },
    enabled: !!galleryId,
  });
};

// ─── Comments: write ─────────────────────────────────────────────

export const useAddComment = (galleryId: string) => {
  const queryClient = useQueryClient();
  const pickerId = getPickerIdentifier();
  const { isDemo, canUseDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ photoId, body }: { photoId: string; body: string }) => {
      // DEMO-4 — client notes on a demo gallery land in the store so
      // the studio's Proofing canvas shows a real thread to resolve.
      if (isDemoId(galleryId)) {
        if (!isDemo && !canUseDemo) {
          throw new Error('This gallery is not available.');
        }
        demoStore.galleryComments.add({
          id: `demo-gc-${crypto.randomUUID().slice(0, 8)}`,
          gallery_id: galleryId,
          photo_id: photoId,
          author_identifier: pickerId,
          author_name: getPickerName(),
          body: body.trim(),
          resolved: false,
          created_at: new Date().toISOString(),
        });
        return;
      }

      // SEC-1 lockdown: the anon insert policy on gallery_comments is
      // gone; the RPC re-checks the gallery is open for proofing.
      // Cast through `any` — types.ts isn't regenerated for the
      // SEC-1 RPCs yet (same pattern as deliveryService).
      const { error } = await (supabase as any).rpc('public_gallery_add_comment', {
        p_gallery_id: galleryId,
        p_photo_id: photoId,
        p_author_identifier: pickerId,
        p_author_name: getPickerName(),
        p_body: body.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-comments', galleryId] });
    },
  });
};

export const useResolveComment = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async ({
      commentId,
      resolved,
    }: { commentId: string; resolved: boolean }) => {
      // DEMO-4 — resolving a client note is part of the proofing beat.
      if (isDemo) {
        assertDemoScoped(commentId, 'Resolving a note on a real gallery');
        demoStore.galleryComments.update(commentId, { resolved });
        return;
      }
      const { error } = await supabase
        .from('gallery_comments')
        .update({ resolved })
        .eq('id', commentId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['gallery-comments'] });
    },
  });
};

// ─── Gallery proofing settings ───────────────────────────────────

export interface ProofingSettings {
  proofing_enabled: boolean;
  proofing_pick_limit: number | null;
  proofing_message: string | null;
  proofing_locked: boolean;
}

export const useUpdateGalleryProofing = (galleryId: string) => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async (settings: Partial<ProofingSettings>) => {
      // DEMO-4 — proofing on/off, pick limit, the client-facing
      // message and the lock all flip in the store. Refused for a real
      // gallery so Preview Studio can't lock a live client out of
      // their picks.
      if (isDemo) {
        assertDemoScoped(galleryId, 'Changing proofing on a real gallery');
        const merged = demoStore.galleries.update(galleryId, settings as any);
        if (!merged) throw new Error("That gallery isn't in this session anymore.");
        return;
      }
      const { error } = await supabase
        .from('galleries')
        .update(settings)
        .eq('id', galleryId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      queryClient.invalidateQueries({ queryKey: ['galleries-by-token'] });
      // DEMO-4 — the editor's own detail key + the client page.
      queryClient.invalidateQueries({ queryKey: ['studio-gallery'] });
      queryClient.invalidateQueries({ queryKey: ['public-gallery', galleryId] });
    },
  });
};

// ─── Realtime subscription for the studio side ───────────────────

/**
 * Subscribes to picks + comments for a gallery and invalidates the
 * react-query caches whenever a row changes. Caller wraps this in a
 * useEffect or component lifecycle.
 */
export function subscribeToProofingChanges(
  galleryId: string,
  onChange: () => void,
) {
  // DEMO-4 — no realtime channel for demo galleries. There are no
  // rows to watch, and the store's `version` counter already drives
  // the re-read. Opening a channel filtered on a non-uuid id just
  // burns a websocket subscription that can never fire.
  if (isDemoId(galleryId)) return () => {};

  // SEC-PORTAL-2 — the live updates ride the same client the reads do,
  // or a signed-in visitor gets the gallery and then never a new comment.
  const reads = portalReads();
  const channel = reads
    .channel(`gallery-proofing-${galleryId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'gallery_picks', filter: `gallery_id=eq.${galleryId}` },
      onChange,
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'gallery_comments', filter: `gallery_id=eq.${galleryId}` },
      onChange,
    )
    .subscribe();

  return () => {
    reads.removeChannel(channel);
  };
}
